import { queryOptions, useMutation, useQuery, useSuspenseQuery } from '@tanstack/react-query'
import { useParams } from '@tanstack/react-router'
import queryClient from '@/lib/queryClient'
import {
  buildAdminQueryKey,
  buildAdminQueryRoot,
  buildScopedAdminQueryKey,
  resolveOrganizationId,
} from '@/lib/organization-scoping'
import {
  createStripeConnectAccountSession,
  resolveStripeConnectAccount,
  getStripeConnectStatus,
  seedStripeConnectTestOnboardingData,
  getStripeConnectPaymentMethods,
  updateStripeConnectPaymentMethod,
} from './stripe-connect.api'
import { requestStripeMigration } from './stripe-migration.api'

export const stripeConnectQueryKeys = {
  all: ({ organizationId } = {}) => [
    buildAdminQueryRoot({ entity: 'stripeConnect' }, { organizationId }),
  ],
  status: ({ organizationId, refresh = false, accountContext = 'current' } = {}) =>
    buildScopedAdminQueryKey(stripeConnectQueryKeys.all({ organizationId })[0], {
      organizationId,
      scope: 'status',
      ...(accountContext !== 'current' ? { accountContext } : {}),
      ...(refresh ? { refresh: true } : {}),
    }),
  paymentMethods: ({ organizationId } = {}) =>
    buildScopedAdminQueryKey(stripeConnectQueryKeys.all({ organizationId })[0], {
      organizationId,
      scope: 'paymentMethods',
    }),
  resolveAccount: ({ organizationId, stripeAccountId, target } = {}) =>
    buildAdminQueryKey(buildAdminQueryRoot({ entity: 'stripeConnect' }), {
      organizationId,
      scope: 'resolveAccount',
      stripeAccountId,
      target,
    }),
}

export const stripeConnectQueries = {
  paymentMethods: ({ organizationId } = {}) =>
    queryOptions({
      queryKey: stripeConnectQueryKeys.paymentMethods({ organizationId }),
      queryFn: () => getStripeConnectPaymentMethods({ organizationId }),
      staleTime: 0,
      retry: false,
    }),
  status: ({ organizationId, refresh = false, accountContext = 'current' } = {}) =>
    queryOptions({
      queryKey: stripeConnectQueryKeys.status({ organizationId, refresh, accountContext }),
      staleTime: refresh ? 0 : undefined,
      queryFn: async () => {
        const status = await getStripeConnectStatus({ organizationId, refresh, accountContext })

        if (refresh) {
          queryClient.setQueryData(
            stripeConnectQueryKeys.status({ organizationId, accountContext }),
            status
          )
        }

        return status
      },
    }),

  resolveAccount: ({ organizationId, stripeAccountId, target } = {}) =>
    queryOptions({
      queryKey: stripeConnectQueryKeys.resolveAccount({
        organizationId,
        stripeAccountId,
        target,
      }),
      queryFn: () => resolveStripeConnectAccount({ organizationId, stripeAccountId, target }),
    }),
}

export function useStripeConnectPaymentMethods(organizationId) {
  return useQuery(stripeConnectQueries.paymentMethods({ organizationId }))
}

export function useUpdateStripeConnectPaymentMethod(organizationId) {
  const queryKey = stripeConnectQueryKeys.paymentMethods({ organizationId })
  return useMutation({
    mutationFn: (preference) => updateStripeConnectPaymentMethod({ organizationId, ...preference }),
    onMutate: () => queryClient.cancelQueries({ queryKey }),
    onSuccess: (data) => queryClient.setQueryData(queryKey, data),
    // A lost response may still have saved at Stripe. Re-read instead of guessing or rolling back.
    onSettled: () => queryClient.invalidateQueries({ queryKey }),
  })
}

export function useStripeConnectStatus({
  organizationId,
  refresh = false,
  accountContext = 'current',
  ...params
} = {}) {
  const { orgId } = useParams({ strict: false })
  const resolvedOrganizationId = resolveOrganizationId(organizationId, orgId)

  return useSuspenseQuery({
    ...stripeConnectQueries.status({
      organizationId: resolvedOrganizationId,
      refresh,
      accountContext,
    }),
    ...params,
  })
}

export function useCreateStripeConnectAccountSessionMutation({
  organizationId: overrideOrganizationId,
  surface,
  accountContext = 'current',
} = {}) {
  const { orgId } = useParams({ strict: false })
  const organizationId = resolveOrganizationId(overrideOrganizationId, orgId)

  return useMutation({
    mutationFn: () =>
      accountContext === 'migration-target'
        ? requestStripeMigration({ organizationId, action: 'account-session', surface })
        : createStripeConnectAccountSession({ organizationId, surface, accountContext }),
  })
}

export function useSeedStripeConnectTestOnboardingDataMutation({
  organizationId: overrideOrganizationId,
} = {}) {
  const { orgId } = useParams({ strict: false })
  const organizationId = resolveOrganizationId(overrideOrganizationId, orgId)

  return useMutation({
    mutationFn: () => seedStripeConnectTestOnboardingData({ organizationId }),
  })
}
